import { settings } from '../core/settings';
import { langInfo } from '../i18n';

export interface Voice {
  pitch?: number;
  rate?: number;
}

/** Voices for the cast, so every character sounds a little different. */
export const VOICES = {
  mom: { pitch: 1.15, rate: 0.95 },
  dad: { pitch: 0.8, rate: 0.92 },
  friend: { pitch: 1.05, rate: 1.0 },
  officer: { pitch: 0.95, rate: 0.95 },
  emma: { pitch: 1.2, rate: 1.0 },
  sales: { pitch: 1.2, rate: 1.12 },
  // Phase D: every character sounds like themselves.
  /** Ms. Lin: calm and even. */
  teacher: { pitch: 1.05, rate: 0.9 },
  /** The school nurse: gentle and slow. */
  nurse: { pitch: 1.12, rate: 0.78 },
  /** Grown-ups around town: warm and unhurried. */
  elder: { pitch: 0.95, rate: 0.85 },
  /** Classmates talk naturally, each a little differently. */
  kid: { pitch: 1.3, rate: 1.05 },
  xiaohua: { pitch: 1.35, rate: 1.15 },
  xiaomei: { pitch: 1.45, rate: 0.92 },
  azhe: { pitch: 1.25, rate: 1.12 },
  xiaoqiang: { pitch: 1.15, rate: 1.08 },
  xiaofang: { pitch: 1.4, rate: 1.04 },
  xiaoyu: { pitch: 1.0, rate: 0.9 },
} satisfies Record<string, Voice>;

function strip(text: string): string {
  return text.replace(/\*\*/g, '').replace(/[「」『』【】“”"()（）…—]/g, ' ');
}

function utter(text: string, lang: string, v: Voice): void {
  if (!settings.voice || typeof speechSynthesis === 'undefined') return;
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(strip(text));
    u.lang = lang;
    u.pitch = v.pitch ?? 1;
    u.rate = v.rate ?? 1;
    const voice = speechSynthesis.getVoices().find((x) => x.lang.replace('_', '-').toLowerCase().startsWith(lang.toLowerCase().slice(0, 2)));
    if (voice) u.voice = voice;
    speechSynthesis.speak(u);
  } catch {
    /* speech is a nicety; never break the game for it */
  }
}

/** Speak a character line in the current UI language. */
export function say(text: string, v: Voice = {}): void {
  let lang = langInfo().speech;
  // Some lines stay in English on purpose (Emma is the English buddy); read them with an English voice.
  if (lang === 'zh-TW' && !/[㐀-鿿]/.test(text)) lang = 'en-US';
  utter(text, lang, v);
}

/** Speak English regardless of UI language (vocabulary practice). */
export function sayEnglish(text: string, rate = 0.85): void {
  utter(text, 'en-US', { rate });
}

export function stopSpeech(): void {
  try {
    speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
}
